import { useCallback, useRef, useEffect } from 'react'

export function useDebounced<T extends (...args: any[]) => any>(
	callback: T,
	wait: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)
	const callbackRef = useRef(callback)

	// Update callback ref when it changes
	useEffect(() => {
		callbackRef.current = callback
	}, [callback])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [])

	const debounced = useCallback(
		(...args: Parameters<T>) => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
			timeoutRef.current = setTimeout(() => {
				callbackRef.current(...args)
			}, wait)
		},
		[wait]
	)

	const cancel = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
	}, [])

	// Attach cancel method to the debounced function
	;(debounced as any).cancel = cancel

	return debounced as ((...args: Parameters<T>) => void) & { cancel: () => void }
}

